import { inject } from '@angular/core';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { PageFilterOperator } from '@common/paged-data/enums/page-filter-operator.enum';
import { PageFilterGroup } from '@common/paged-data/types/page-filter-group.type';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { PageSort } from '@common/paged-data/types/page-sort.type';
import { SortDirection } from '@common/paged-data/types/sort-direction.type';
import { DataList } from '@portal-core/data/collection/models/data-list.model';
import { GetDataListMethods } from '@portal-core/data/collection/models/get-data-list-methods.model';
import { GetPageDataMethods } from '@portal-core/data/collection/models/get-page-data-methods.model';
import { CollectionDataServiceBase } from '@portal-core/data/collection/services/collection-data.service.base';
import { CollectionPredicate, CollectionPropertyPairs } from '@portal-core/data/collection/types/collection-property-args.type';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { McModel } from '@portal-core/data/common/models/mc-model.model';
import { DataService } from '@portal-core/data/common/services/data.service';
import { ModelId, ModelIds } from '@portal-core/data/common/types/mc-model.type';
import { IResettable } from '@portal-core/util/resettable.decorator';
import { keyBy } from 'lodash';
import { first, map, Observable, of, switchMap } from 'rxjs';

/**
 * Provides common functionality for a service that interacts with a collection-data service (CollectionDataServiceBase).
 * Extend this service in collection service classes.
 */
export abstract class CollectionServiceBase<T extends McModel> implements IResettable {
  protected collectionDataService: CollectionDataServiceBase<T>;
  protected readonly dataService = inject(DataService);

  constructor(collectionDataService: CollectionDataServiceBase<T>) {
    this.collectionDataService = collectionDataService;
    this.collectionDataService.addDefaultItems$();
  }

  protected fetchItemById$(_itemId: ModelId): Observable<T> { throw new Error('fetchItemById$ is not implemented'); }
  protected fetchItemsById$(_itemIds: ModelIds): Observable<T[]> { throw new Error('fetchItemsById$ is not implemented'); }

  reset$(): Observable<void> {
    return this.collectionDataService.reset$();
  }

  // Items
  getItems<S extends T = T>(): Dictionary<S> {
    return this.collectionDataService.getItems<S>();
  }

  getItems$<S extends T = T>(): Observable<Dictionary<S>> {
    return this.collectionDataService.getItems$<S>();
  }

  getItemsArray<S extends T = T>(): S[] {
    const items = this.getItems<S>();
    return items ? Object.values(items) : [];
  }

  getItemsArray$<S extends T = T>(): Observable<S[]> {
    return this.getItems$<S>().pipe(
      map(item => item ? Object.values(item) : [])
    );
  }

  addItems$<S extends T = T>(items: Dictionary<Partial<S>> | Partial<S>[]): Observable<void> {
    return this.collectionDataService.addItems$<S>(items);
  }

  updateItems$<S extends T = T>(items: Dictionary<Partial<S>> | Partial<S>[]): Observable<void> {
    return this.collectionDataService.updateItems$<S>(items);
  }

  /**
   * Evicts items from the local cache only — reach for this when the items still exist on the server
   * but should no longer be held locally: a rename, a full refresh, hiding an item, dropping a stale
   * entry, or clearing a cache so it can be refetched. Grid and paged-list totals are left as the server
   * reported them, so paginators keep their counts.
   *
   * For items that have actually been deleted, use {@link deleteItems$} instead so the counts stay accurate.
   * @param items the items (or their ids) to evict from the cache.
   */
  removeItems$(items: Dictionary<T> | T[] | ModelId[]): Observable<void> {
    // const itemIds = items.map(item => typeof item === 'string' || typeof item === 'number' ? item : item.Id);
    // return this.collectionDataService.removeItems$(itemIds);
    return this.collectionDataService.removeItems$(items);
  }

  /**
   * Removes items that have been permanently deleted — call this right after a successful server-side
   * delete. As well as dropping the items from the cache, it strips them from every grid and paged data
   * list and decrements the stored totals so paginators stay accurate, and it can be observed via
   * `CollectionActionsService.deleteItems$` for cascade side-effects.
   *
   * For a cache eviction where the items still exist on the server, use {@link removeItems$} instead.
   * @param items the items (or their ids) that were deleted.
   */
  deleteItems$(items: Dictionary<T> | T[] | ModelId[]): Observable<void> {
    return this.collectionDataService.deleteItems$(items);
  }

  addItemsToListById$(listName: string, listId: ModelId, items: T[]): Observable<void> {
    return this.collectionDataService.addItemsToListById$(listName, listId, items);
  }

  getDataListItems$(listName: string, listId: ModelId, methods: GetDataListMethods<T>, options?: GetDataOptions): Observable<T[]> {
    return this.dataService.getDataListItems$<T>(listName, listId, this.collectionDataService, methods, options);
  }

  // Item by id
  getItemById<S extends T = T>(itemId: ModelId): S {
    return this.collectionDataService.getItemById<S>(itemId);
  }

  getItemById$<S extends T = T>(itemId: ModelId, options: GetDataOptions = null): Observable<S> {
    return this.dataService.getData<T>({
      get: () => this.collectionDataService.getItemById$<T>(itemId),
      fetch: () => this.fetchItemById$(itemId),
      set: newItem => this.collectionDataService.addItems$({ [newItem.Id]: newItem })
    }, options) as Observable<S>;
  }

  // The by-properties methods forward to the data service through a loose function type: a generic
  // rest tuple widens to `unknown[]` when spread, so the delegate can't be called generically — but
  // the arguments are already validated by each method's own (public) signature.

  /**
   * Returns the first cached item that matches every one of the given `(property-path, value)` pairs,
   * or `undefined` if none match. The arguments are a flat, repeating list — a property path followed
   * by the value it must equal, repeated — and are type-checked against the item type: each path must
   * be a property of the item (use a dotted path to reach a nested property, e.g. `'User.Id'`), and
   * each value must match the type at that path. Values are compared by strict equality.
   *
   * This reads a one-time snapshot of the current state. Use {@link getItemByProperties$} for a value
   * that re-emits as the collection changes, or {@link getItemByPredicate} to match on an arbitrary
   * condition instead of property equality.
   *
   * @example
   * this.reviewFilesService.getItemByProperties('ReviewPackageId', reviewPackageId, 'Path', path);
   */
  getItemByProperties<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): T {
    return (this.collectionDataService.getItemByProperties as (...args: readonly unknown[]) => T)(...args);
  }

  /**
   * Observable form of {@link getItemByProperties}: emits the first item matching every given
   * `(property-path, value)` pair, and re-emits whenever the collection changes (emitting `undefined`
   * when nothing matches). The paired arguments are typed exactly as in {@link getItemByProperties}.
   *
   * @example
   * this.reviewPackageUsersService.getItemByProperties$('UserId', userId, 'ReviewPackageId', reviewPackageId);
   */
  getItemByProperties$<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): Observable<T> {
    return (this.collectionDataService.getItemByProperties$ as (...args: readonly unknown[]) => Observable<T>)(...args);
  }

  /**
   * Returns every cached item that matches all of the given `(property-path, value)` pairs (an empty
   * array when none match). Takes the same paired, type-checked arguments as
   * {@link getItemByProperties}, but returns all matches rather than only the first.
   *
   * @example
   * this.messagesService.getItemsByProperties('ConversationId', conversationId);
   */
  getItemsByProperties<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): T[] {
    return (this.collectionDataService.getItemsByProperties as (...args: readonly unknown[]) => T[])(...args);
  }

  /**
   * Observable form of {@link getItemsByProperties}: emits the array of all items matching the given
   * `(property-path, value)` pairs, and re-emits whenever the collection changes.
   */
  getItemsByProperties$<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): Observable<T[]> {
    return (this.collectionDataService.getItemsByProperties$ as (...args: readonly unknown[]) => Observable<T[]>)(...args);
  }

  /**
   * Returns the first cached item for which `predicate` returns `true`, or `undefined` if none do.
   * Use this to match on an arbitrary condition; for plain property equality prefer
   * {@link getItemByProperties}, which type-checks the property names and their values.
   *
   * @example
   * this.branchesService.getItemByPredicate(branch => branch.Name === name && !branch.Default);
   */
  getItemByPredicate(predicate: CollectionPredicate<T>): T {
    return this.collectionDataService.getItemByPredicate(predicate);
  }

  // Lists
  getListById(listName: string, listId: ModelId): DataList {
    return this.collectionDataService.getListById(listName, listId);
  }

  getListById$(listName: string, listId: ModelId): Observable<DataList> {
    return this.collectionDataService.getListById$(listName, listId);
  }

  clearLists$(): Observable<void> {
    return this.collectionDataService.clearLists$();
  }

  setListById$(listName: string, listId: ModelId, items: T[]): Observable<void> {
    return this.collectionDataService.setListById$(listName, listId, items);
  }

  /*
   * Paged Data Lists
   */
  // Pages
  getPagedDataListItems(pagedDataListId: string): T[] {
    return this.collectionDataService.getPagedDataListItems(pagedDataListId);
  }

  getPagedDataListItems$(pagedDataListId: string): Observable<T[]> {
    return this.collectionDataService.getPagedDataListItems$(pagedDataListId);
  }

  loadMorePagedDataListItems$(dataListId: string, pageSize: number, cursor: number, methods?: GetPageDataMethods<T>, options?: GetDataOptions): Observable<void> {
    let pageIndex = this.collectionDataService.getPagedDataListPageIndex(dataListId);
    if (typeof pageIndex !== 'number') {
      pageIndex = -1; // The page index will be incremented to load the next page so default to -1 so that it is incremented to 0
    }

    const total = this.collectionDataService.getPagedDataListTotal(dataListId);
    const length = this.collectionDataService.getPagedDataListLength(dataListId);
    const allPagesLoaded = this.collectionDataService.getPagedDataListAllPagesLoaded(dataListId);

    if (typeof length !== 'number' || typeof total !== 'number' || length < total || (total === -1 && !allPagesLoaded)) {
      return this.loadPagedDataListItems$(dataListId, pageIndex + 1, pageSize, cursor, methods, options);
    } else {
      return of(null);
    }
  }

  loadPagedDataListItems$(dataListId: string, pageIndex: number, pageSize: number, cursor: number, methods?: GetPageDataMethods<T>, options?: GetDataOptions): Observable<void> {
    const filterOrder = this.collectionDataService.getPagedDataListOrder(dataListId);

    const filter = this.buildPageFilter(
      pageIndex,
      pageSize,
      cursor,
      filterOrder ? filterOrder.by : null,
      filterOrder ? filterOrder.dir : null,
      this.collectionDataService.getPagedDataListFilters(dataListId)
    );

    return methods.fetch(filter, options).pipe(
      switchMap(page => {
        return page ? this.collectionDataService.addDataListPage$(dataListId, page) : of(null);
      })
    );
  }

  resetPagedDataList$(dataListId: string): Observable<void> {
    return this.collectionDataService.resetPagedDataList$(dataListId);
  }

  clearPagedDataList$(dataListId: string): Observable<void> {
    return this.collectionDataService.clearPagedDataList$(dataListId);
  }

  removePagedDataListItems$(dataListId: string, itemIds: ModelIds): Observable<void> {
    return this.collectionDataService.removePagedDataListItems$(dataListId, itemIds);
  }

  getPagedDataListAllPagesLoaded(dataListId: string): boolean {
    return this.collectionDataService.getPagedDataListAllPagesLoaded(dataListId);
  }

  getPagedDataListAllPagesLoaded$(dataListId: string): Observable<boolean> {
    return this.collectionDataService.getPagedDataListAllPagesLoaded$(dataListId);
  }

  // Filters
  getPagedDataListFilters$(pagedDataListId: string): Observable<Dictionary<PageFilterGroup>> {
    return this.collectionDataService.getPagedDataListFilters$(pagedDataListId);
  }

  // Filter by name
  getPagedDataListFilterByName(pagedDataListId: string, name: string): PageFilterGroup {
    return this.collectionDataService.getPagedDataListFilterByName(pagedDataListId, name);
  }

  getPagedDataListFilterByName$(pagedDataListId: string, name: string): Observable<PageFilterGroup> {
    return this.collectionDataService.getPagedDataListFilterByName$(pagedDataListId, name);
  }

  setPagedDataListFilterByName$(pagedDataListId: string, name: string, value: PageFilterGroup): Observable<void> {
    return this.collectionDataService.setPagedDataListFilterByName$(pagedDataListId, name, value);
  }

  // Order
  getPagedDataListOrder(pagedDataListId: string): PageSort {
    return this.collectionDataService.getPagedDataListOrder(pagedDataListId);
  }

  getPagedDataListOrder$(pagedDataListId: string): Observable<PageSort> {
    return this.collectionDataService.getPagedDataListOrder$(pagedDataListId);
  }

  setPagedDataListOrder$(dataListId: string, orderBy: string, orderDirection: SortDirection): Observable<void> {
    return this.collectionDataService.setPagedDataListOrder$(dataListId, orderBy, orderDirection);
  }

  /*
   * Grids
   */

  /**
   * Returns an observable for a page's items in a grid.
   * @param gridId The id of the grid to get the items for.
   * @param pageIndex The page index to get the items for.
   */
  getGridItems$(gridId: string, pageIndex: number): Observable<T[]> {
    return this.collectionDataService.getGridItems$(gridId, pageIndex);
  }

  /**
   * Returns an observable that emits once and completes after the data is fetched and loaded into the data store.
   * If the page already exists then the fetch is not made and the observable immediately completes.
   * @param gridId The id of the grid to the load the items for.
   * @param pageIndex  The page index to load the items for.
   * @param pageSize The number of items to load.
   * @param methods The fetch method configuration which defines how the grid page's data is loaded.
   * @param options The data fetching options passed to the fetch method.
   */
  loadGridItems$(gridId: string, pageIndex: number, pageSize: number, cursor: number, methods?: GetPageDataMethods<T>, options?: GetDataOptions): Observable<void> {
    const filterOrder = this.collectionDataService.getGridOrder(gridId);

    const filter = this.buildPageFilter(
      pageIndex,
      pageSize,
      cursor,
      filterOrder ? filterOrder.by : null,
      filterOrder ? filterOrder.dir : null,
      this.collectionDataService.getGridFilters(gridId)
    );

    const gridPage = this.collectionDataService.getGridPage(gridId, pageIndex);

    if (!gridPage || options?.forceApiRequest) {
      return methods.fetch(filter, options).pipe(
        switchMap(page => {
          return page ? this.collectionDataService.addGridPage$(gridId, page, pageSize) : of(null);
        })
      );
    } else {
      return of(null);
    }
  }

  /**
   * Removes all the data and config associated with the grid.
   * @param gridId The id of the grid.
   */
  resetGrid$(gridId: string): Observable<void> {
    return this.collectionDataService.resetGrid$(gridId);
  }

  /**
   * Removes the pages from the grid.
   * @param gridId The id of the grid.
   */
  clearGrid$(gridId: string): Observable<void> {
    return this.collectionDataService.clearGrid$(gridId);
  }

  /**
   * Returns the cursor for page in a grid.
   * @param gridId The id of the grid to get the cursor for.
   * @param pageIndex The page index to get the cursor for.
   */
  getGridCursor(gridId: string, pageIndex: number): number {
    return this.collectionDataService.getGridCursor(gridId, pageIndex);
  }

  /**
   * Returns the total number of items across every page of the grid.
   * @param gridId The id of the grid.
   */
  getGridTotal(gridId: string): number {
    return this.collectionDataService.getGridTotal(gridId);
  }

  /**
   * Returns an observable of the total number of items across every page of the grid.
   * @param gridId The id of the grid.
   */
  getGridTotal$(gridId: string): Observable<number> {
    return this.collectionDataService.getGridTotal$(gridId);
  }

  /**
   * Returns the limited total number of items across every page of the grid.
   * The server will sometimes return a limited total number of items for performance reasons.
   * @param gridId The id of the grid.
   */
  getGridLimitedTotal(gridId: string): number {
    return this.collectionDataService.getGridLimitedTotal(gridId);
  }

  /**
   * Returns an observable of the limited total number of items across every page of the grid.
   * The server will sometimes return a limited total number of items for performance reasons.
   * @param gridId The id of the grid.
   */
  getGridLimitedTotal$(gridId: string): Observable<number> {
    return this.collectionDataService.getGridLimitedTotal$(gridId);
  }

  /**
   * Returns the filters on the grid.
   * @param gridId The id of the grid.
   */
  getGridFilters(gridId: string): Dictionary<PageFilterGroup> {
    return this.collectionDataService.getGridFilters(gridId);
  }

  /**
   * Returns an observable of the filters on the grid.
   * @param gridId The id of the grid.
   */
  getGridFilters$(gridId: string): Observable<Dictionary<PageFilterGroup>> {
    return this.collectionDataService.getGridFilters$(gridId);
  }

  /**
   * Returns the filter on the grid with the given name.
   * @param gridId The id of the grid.
   * @param name: The name of the filter.
   */
  getGridFilterByName(gridId: string, name: string): PageFilterGroup {
    return this.collectionDataService.getGridFilterByName(gridId, name);
  }

  /**
   * Returns an observable of the filter on the grid with the given name.
   * @param gridId The id of the grid.
   * @param name: The name of the filter.
   */
  getGridFilterByName$(gridId: string, name: string): Observable<PageFilterGroup> {
    return this.collectionDataService.getGridFilterByName$(gridId, name);
  }

  /**
   * Sets the filter on the grid with the given name.
   * @param gridId The id of the grid.
   * @param name: The name of the filter.
   * @param value: The value of the filter.
   */
  setGridFilterByName$(gridId: string, name: string, value: PageFilterGroup): Observable<void> {
    return this.collectionDataService.setGridFilterByName$(gridId, name, value);
  }

  /**
   * Clear all the filters on the grid.
   * @param gridId The id of the grid.
   */
  clearGridFilters$(gridId: string): Observable<void> {
    return this.collectionDataService.clearGridFilters$(gridId);
  }

  /**
   * Returns the page size of the grid.
   * @param gridId The id of the grid.
   */
  getGridPageSize(gridId: string): number {
    return this.collectionDataService.getGridPageSize(gridId);
  }

  /**
   * Returns an observable of the page size of the grid.
   * @param gridId The id of the grid.
   */
  getGridPageSize$(gridId: string): Observable<number> {
    return this.collectionDataService.getGridPageSize$(gridId);
  }

  /**
   * Sets the page size of the grid.
   * @param gridId The id of the grid.
   * @param pageSize The new page size of the grid.
   */
  setGridPageSize$(gridId: string, pageSize: number): Observable<void> {
    return this.collectionDataService.setGridPageSize$(gridId, pageSize);
  }

  /**
   * Returns the sort order on the grid.
   * @param gridId The id of the grid.
   */
  getGridOrder(gridId: string): PageSort {
    return this.collectionDataService.getGridOrder(gridId);
  }

  /**
   * Returns an observable of the sort order on the grid.
   * @param gridId The id of the grid.
   */
  getGridOrder$(gridId: string): Observable<PageSort> {
    return this.collectionDataService.getGridOrder$(gridId);
  }

  /**
   * Sets the sort order of the grid.
   * @param gridId The id of the grid.
   * @param orderBy The name of the column to order the grid by.
   * @param orderDirection The direction to sort the column by.
   */
  setGridOrder$(gridId: string, orderBy: string, orderDirection: SortDirection): Observable<void> {
    return this.collectionDataService.setGridOrder$(gridId, orderBy, orderDirection);
  }

  /**
   * Returns the visible columns for the grid.
   * @param gridId The id of the grid.
   */
  getGridVisibleColumns(gridId: string): string[] {
    return this.collectionDataService.getGridVisibleColumns(gridId);
  }

  /**
   * Returns an observable of the visible columns for the grid.
   * @param gridId The id of the grid.
   */
  getGridVisibleColumns$(gridId: string): Observable<string[]> {
    return this.collectionDataService.getGridVisibleColumns$(gridId);
  }

  /**
   * Sets the visible columns for the grid.
   * @param gridId The id of the grid.
   * @param visibleColumns An array of column names to be displayed in the grid. The order of the names is used as the order of the columns in the grid.
   */
  setGridVisibleColumns$(gridId: string, visibleColumns: string[]): Observable<void> {
    return this.collectionDataService.setGridVisibleColumns$(gridId, visibleColumns);
  }

  /**
   * Makes a request to the API server for the latest data for the item and updates the data store.
   * @param itemId: The id of the item.
   */
  refreshItemById(itemId: ModelId) {
    // Get the item but force an api request. And stop listening after the item is fetched
    this.getItemById$(itemId, { forceApiRequest: true }).pipe(
      first()
    ).subscribe();
  }

  /*
   * Bulk item loading
   */
  loadItems$(itemIds: ModelIds, options: GetDataOptions = null): Observable<void> {
    return this.bulkLoadItems$(
      () => {
        const itemIdsToFetch = (itemIds ?? []).filter(itemId => {
          if (itemId === 0) {
            return false;
          }

          if (options?.forceApiRequest) {
            return true;
          }

          const item = this.getItemById(itemId);

          // If the item has expired OR there is no item then fetch this item
          return item ? this.dataService.cacheHasExpired(item, null, { maxAgeMS: DataService.DefaultDataMaxAgeMS }) : true;
        });

        if (itemIdsToFetch.length > 0) {
          return this.fetchItemsById$(itemIdsToFetch);
        }
      }
    );
  }

  protected bulkLoadItems$(fetch: () => Observable<T[]>): Observable<void> {
    // Fetch is allowed to return undefined if it determined nothing needs to be loaded. In that case use of(null) to have an observable to work with
    return (fetch() || of(null)).pipe(
      switchMap(newItems => {
        if (newItems && newItems.length > 0) {
          return this.collectionDataService.addItems$(keyBy(newItems, 'Id'));
        } else {
          return of(null);
        }
      })
    );
  }

  protected buildPageFilter(pageIndex: number, pageSize: number, cursor: number, orderBy: string, orderDirection: SortDirection, filters: Dictionary<PageFilterGroup>): PageFilter {
    return {
      Id: 'collection-page',
      Type: PageFilterGroupType.Custom,
      Cursor: cursor,
      PageNumber: pageIndex,
      PerPage: pageSize,
      OrderBy: orderBy,
      OrderDirection: orderDirection,
      Operator: PageFilterOperator.And,
      FilterGroups: filters ? Object.values(filters).filter(filterGroup => {
        // The old filter format without an Id should have been migrated out of the persisted data. Just in case filter out any old filters
        if (!filterGroup?.Id) {
          return false;
        }

        // Filter out groups that do not contain at least one filter or one filter group
        return filterGroup && ((filterGroup.Filters && filterGroup.Filters.length > 0) || (filterGroup.FilterGroups && filterGroup.FilterGroups.length > 0));
      }).reduce((allFilters, filterOptions) => {
        return allFilters.concat(filterOptions);
      }, []) : []
    };
  }
}
